// Catálogo de tiendas y productos con el que se acota el alcance de una promoción. El lambda junta
// las de todas las cadenas del tenant, así que la pantalla lo resuelve con UNA sola lectura.
import { useQuery } from '@tanstack/react-query';
import type {
    ScopeCatalogProduct,
    ScopeCatalogResponse,
    ScopeCatalogStore,
} from '@/api/services/loan-offerings/contract';
import { getScopeCatalog } from '@/api/services/loan-offerings/loan-offerings.client';

/** Una opción elegible del alcance. `value` es lo que se guarda: el `storeId` o el `sku`. */
export interface ScopeOption {
    value: string;
    /** Lo que identifica a la opción para una persona: el nombre de la tienda, marca + modelo. */
    label: string;
    /** El renglón chico de abajo — el id de la tienda, el SKU del producto. */
    detail: string;
    /** Texto en minúsculas contra el que filtra el buscador. */
    haystack: string;
    /** La cadena dueña de la opción, cuando el catálogo la trae. */
    retailerId?: string;
}

export interface ScopeCatalog {
    /** Alfabéticas por `label`, sin repetidos entre cadenas. */
    options: ScopeOption[];
    isLoading: boolean;
    /** La lectura falló: no hay catálogo y hay que dejar teclear el id. */
    hasError: boolean;
    /** El lambda cortó el barrido: la lista vino incompleta y hay que dejar teclear el id. */
    isTruncated: boolean;
    /** Las cadenas de cada `value`, del catálogo completo; un SKU puede venderse en varias. */
    retailersByValue?: ReadonlyMap<string, ReadonlySet<string>>;
}

// Las dos dimensiones comparten esta key: montar los dos pickers dispara UNA sola llamada.
const SCOPE_CATALOG_KEY = ['loan-offerings', 'promotions', 'scope-catalog'] as const;
const STALE_MS = 5 * 60 * 1000;

/** Estado de la lectura, sin atarse al tipo de dato que devuelve. */
interface QueryState {
    isLoading: boolean;
    isError: boolean;
    data: ScopeCatalogResponse | undefined;
}

/** La lectura única del catálogo; las dos dimensiones la comparten por la query key. */
function useScopeCatalogQuery(): QueryState {
    return useQuery({
        queryKey: SCOPE_CATALOG_KEY,
        queryFn: () => getScopeCatalog(),
        staleTime: STALE_MS,
    });
}

/** Las tiendas del tenant; con cadenas elegidas, solo las de esas cadenas. */
export function useStoreCatalog(retailerIds: readonly string[] | null = null): ScopeCatalog {
    const query = useScopeCatalogQuery();
    return catalogOfRetailers(query, (query.data?.stores ?? []).map(toStoreOption), retailerIds);
}

/** Los productos del tenant; con cadenas elegidas, solo los de esas cadenas. */
export function useProductCatalog(retailerIds: readonly string[] | null = null): ScopeCatalog {
    const query = useScopeCatalogQuery();
    const products = (query.data?.products ?? []).map(toProductOption);
    return catalogOfRetailers(query, products, retailerIds);
}

/** Filtra por cadenas antes del dedup, para no perder un SKU que vende otra cadena. */
function catalogOfRetailers(
    query: QueryState,
    options: readonly ScopeOption[],
    retailerIds: readonly string[] | null,
): ScopeCatalog {
    return {
        ...collect(query, optionsOfRetailers(options, retailerIds)),
        retailersByValue: indexRetailersByValue(options),
    };
}

/** Las cadenas en las que aparece cada `value`. */
export function indexRetailersByValue(
    options: readonly ScopeOption[],
): Map<string, ReadonlySet<string>> {
    const index = new Map<string, Set<string>>();
    for (const option of options) {
        if (option.retailerId === undefined) continue;
        const retailers = index.get(option.value) ?? new Set<string>();
        retailers.add(option.retailerId);
        index.set(option.value, retailers);
    }
    return index;
}

/** Las opciones de esas cadenas; sin cadenas elegidas, todas. */
export function optionsOfRetailers(
    options: readonly ScopeOption[],
    retailerIds: readonly string[] | null,
): ScopeOption[] {
    if (retailerIds === null || retailerIds.length === 0) return [...options];
    const allowed = new Set(retailerIds);
    return options.filter(
        (option) => option.retailerId !== undefined && allowed.has(option.retailerId),
    );
}

/** El código interno entra al buscador aunque no se pinte: es como la nombra el operador. */
function toStoreOption(store: ScopeCatalogStore): ScopeOption {
    return {
        value: store.storeId,
        label: store.name,
        detail: store.storeId,
        haystack: `${store.name} ${store.code} ${store.storeId}`.toLowerCase(),
        retailerId: store.retailerId,
    };
}

/** El producto se reconoce por marca y modelo; lo que se guarda es el SKU. */
function toProductOption(product: ScopeCatalogProduct): ScopeOption {
    return {
        value: product.sku,
        label: `${product.brand} ${product.model}`.trim(),
        detail: product.sku,
        haystack: `${product.brand} ${product.model} ${product.sku}`.toLowerCase(),
        retailerId: product.retailerId,
    };
}

/** Arma el catálogo: dedup por `value`, orden alfabético y el estado de la lectura. */
function collect(query: QueryState, options: ScopeOption[]): ScopeCatalog {
    const seen = new Set<string>();
    const unique = options.filter((option) => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
    });

    return {
        options: unique.sort((left, right) => byLabel(left, right)),
        isLoading: query.isLoading,
        hasError: query.isError,
        isTruncated: query.data?.truncated ?? false,
    };
}

/** Orden alfabético por nombre; el detalle desempata cuando dos se llaman igual. */
function byLabel(left: ScopeOption, right: ScopeOption): number {
    const byName = left.label.localeCompare(right.label, 'es-MX');
    return byName === 0 ? left.detail.localeCompare(right.detail, 'es-MX') : byName;
}
